import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { getPublicProfileUrl, getVanityUrl, getBestPublicUrl, isProductionDomain, getDisplayDomain } from '../lib/publicUrl';
import { validateUsername, USERNAME_MIN_LENGTH } from '../lib/usernameValidation';
import { toast } from 'sonner';
import { 
  Gear,
  CaretLeft,
  Globe,
  Copy,
  Check,
  Lock,
  Eye,
  Warning,
  ArrowSquareOut,
  LinkSimple,
  User,
  CircleNotch,
  CheckCircle,
  XCircle,
  At,
  Camera,
  Trash,
  FloppyDisk,
  Info,
  EnvelopeSimple
} from 'phosphor-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import GeniLoader from '../components/GeniLoader';
import ImageCropper from '../components/ImageCropper';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import IntegrationsSettings from '../components/IntegrationsSettings';

// GENI Brain logo fallback for avatars
const GENI_BRAIN_FALLBACK = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/gtg4vc6a_purebrain.png";

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const { t, language } = useLanguage();
  const { user } = useAuth();
  
  // Public profile settings - current UI state
  const [publicProfileEnabled, setPublicProfileEnabled] = useState(false);
  // Original saved value from backend
  const [originalPublicProfileEnabled, setOriginalPublicProfileEnabled] = useState(false);
  // Username for vanity URL
  const [username, setUsername] = useState(null);
  const [originalUsername, setOriginalUsername] = useState(null);
  // Username editing state
  const [usernameInput, setUsernameInput] = useState('');
  const [usernameValidation, setUsernameValidation] = useState({ valid: true, error: null });
  const [usernameAvailability, setUsernameAvailability] = useState(null); // null | 'checking' | 'available' | 'taken' | 'error'
  const [usernameSaving, setUsernameSaving] = useState(false);
  const availabilityCheckTimeout = useRef(null);
  // Save state for public profile
  const [publicProfileSaving, setPublicProfileSaving] = useState(false);
  const [publicProfileSaveStatus, setPublicProfileSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'
  const [linkCopied, setLinkCopied] = useState(false);
  const [isPro, setIsPro] = useState(false);

  // Lifecycle/Engagement emails preference (activation, re-engagement, milestones, monthly summary)
  // Separate from Options Alerts and Product Updates preferences
  const [lifecycleEmailsEnabled, setLifecycleEmailsEnabled] = useState(true);
  const [lifecycleSaving, setLifecycleSaving] = useState(false);
  
  // Avatar state
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarInputRef = useRef(null);
  
  // Image cropper state
  const [showCropper, setShowCropper] = useState(false);
  const [cropperFile, setCropperFile] = useState(null);
  
  // Track if public profile settings changed (toggle or username)
  const hasUsernameChanges = usernameInput.trim() !== (originalUsername || '');
  const hasPublicProfileChanges = publicProfileEnabled !== originalPublicProfileEnabled || hasUsernameChanges;

  useEffect(() => {
    fetchPublicProfileSettings();
  }, []);

  const fetchPublicProfileSettings = async () => {
    try {
      const response = await api.get('/user/profile');
      const savedValue = response.data.public_profile_enabled || false;
      setPublicProfileEnabled(savedValue);
      setOriginalPublicProfileEnabled(savedValue);
      // Fetch username for vanity URL
      const savedUsername = response.data.username || '';
      setUsername(savedUsername || null);
      setOriginalUsername(savedUsername || null);
      setUsernameInput(savedUsername);
      // Fetch avatar URL
      setAvatarUrl(response.data.public_profile_avatar_url || null);
      // Fetch PRO status
      setIsPro(response.data.subscription_status === 'pro' || response.data.is_demo === true);
      // Lifecycle emails preference - missing/undefined means enabled (default)
      setLifecycleEmailsEnabled(response.data.lifecycle_emails_enabled !== false);
    } catch (error) {
      console.error('Error fetching public profile settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Toggle Lifecycle/Engagement emails preference - saves immediately
  const handleLifecycleEmailsToggle = async (enabled) => {
    setLifecycleEmailsEnabled(enabled);
    setLifecycleSaving(true);
    try {
      await api.post('/user/lifecycle-email-preference', { lifecycle_emails_enabled: enabled });
      toast.success(
        enabled
          ? (language === 'ptBR' ? 'Emails de acompanhamento ativados' : 'Follow-up emails turned on')
          : (language === 'ptBR' ? 'Emails de acompanhamento desativados' : 'Follow-up emails turned off')
      );
    } catch (error) {
      console.error('Error saving lifecycle email preference:', error);
      setLifecycleEmailsEnabled(!enabled);
      toast.error(language === 'ptBR' ? 'Falha ao salvar preferência' : 'Failed to save preference');
    } finally {
      setLifecycleSaving(false);
    }
  };

  // Handle avatar file selection - opens cropper instead of direct upload
  const handleAvatarSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or WEBP image');
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large. Please upload an image smaller than 5MB');
      return;
    }
    
    // Open cropper with selected file
    setCropperFile(file);
    setShowCropper(true);
    
    // Reset file input so same file can be selected again if needed
    if (avatarInputRef.current) {
      avatarInputRef.current.value = '';
    }
  };
  
  // Handle cropped image save - uploads the cropped result
  const handleCroppedImageSave = async (croppedBlob) => {
    setShowCropper(false);
    setCropperFile(null);
    setAvatarUploading(true);
    
    try {
      // Create a File from the Blob for upload
      const croppedFile = new File([croppedBlob], 'avatar.jpg', { type: 'image/jpeg' });
      
      const formData = new FormData();
      formData.append('file', croppedFile);
      
      const response = await api.post('/user/profile/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setAvatarUrl(response.data.avatar_url);
        toast.success(language === 'ptBR' ? 'Foto de perfil atualizada' : 'Profile picture updated');
      }
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error(error.response?.data?.detail || (language === 'ptBR' ? 'Falha ao enviar imagem' : 'Failed to upload image'));
    } finally {
      setAvatarUploading(false);
    }
  };
  
  // Handle cropper cancel
  const handleCropperCancel = () => {
    setShowCropper(false);
    setCropperFile(null);
  };
  
  // Handle avatar removal
  const handleRemoveAvatar = async () => {
    setAvatarUploading(true);
    try {
      await api.delete('/user/profile/avatar');
      setAvatarUrl(null);
      toast.success('Profile picture removed');
    } catch (error) {
      console.error('Error removing avatar:', error);
      toast.error('Failed to remove profile picture');
    } finally {
      setAvatarUploading(false);
    }
  };

  // Handle toggle change - just updates UI state, doesn't save
  const handlePublicProfileToggle = (enabled) => {
    setPublicProfileEnabled(enabled);
    // Clear any previous save status when user makes a new change
    setPublicProfileSaveStatus(null);
  };

  // Save public profile settings to backend
  const handleSavePublicProfile = async () => {
    // Validate username before saving if it changed
    if (hasUsernameChanges && usernameInput.trim()) {
      const validation = validateUsername(usernameInput);
      if (!validation.valid) {
        toast.error(validation.error);
        return;
      }
      if (usernameAvailability === 'taken') {
        toast.error(language === 'ptBR' ? 'Nome de usuário já está em uso' : 'Username is already taken');
        return;
      }
      if (usernameAvailability === 'checking') {
        toast.error(language === 'ptBR' ? 'Aguarde a verificação do nome de usuário' : 'Please wait for username check');
        return;
      }
    }
    
    setPublicProfileSaving(true);
    setPublicProfileSaveStatus('saving');
    
    try {
      const updateData = {
        public_profile_enabled: publicProfileEnabled
      };
      
      // Include username if it changed
      if (hasUsernameChanges) {
        updateData.username = usernameInput.trim() || null;
      }
      
      await api.put('/user/profile', updateData);
      
      // Update original values to match saved values
      setOriginalPublicProfileEnabled(publicProfileEnabled);
      if (hasUsernameChanges) {
        const savedUsername = usernameInput.trim() || null;
        setOriginalUsername(savedUsername);
        setUsername(savedUsername);
      }
      setPublicProfileSaveStatus('saved');
      
      // Build success message
      let successMsg;
      if (hasUsernameChanges && publicProfileEnabled !== originalPublicProfileEnabled) {
        successMsg = language === 'ptBR' ? 'Configurações salvas!' : 'Settings saved!';
      } else if (hasUsernameChanges) {
        successMsg = language === 'ptBR' ? 'Nome de usuário atualizado!' : 'Username updated!';
      } else {
        successMsg = publicProfileEnabled 
          ? (language === 'ptBR' ? 'Perfil público ativado!' : 'Public profile enabled!')
          : (language === 'ptBR' ? 'Perfil público desativado' : 'Public profile disabled');
      }
      
      toast.success(successMsg);
      
      // Clear "saved" status after 3 seconds
      setTimeout(() => {
        setPublicProfileSaveStatus(null);
      }, 3000);
      
    } catch (error) {
      console.error('Error updating public profile:', error);
      setPublicProfileSaveStatus('error');
      
      // Show specific error message if available
      const errorMsg = error.response?.data?.detail || (language === 'ptBR' 
        ? 'Falha ao salvar configurações' 
        : 'Failed to save settings');
      toast.error(errorMsg);
    } finally {
      setPublicProfileSaving(false);
    }
  };

  // Revert public profile changes
  const handleRevertPublicProfile = () => {
    setPublicProfileEnabled(originalPublicProfileEnabled);
    setUsernameInput(originalUsername || '');
    setUsernameValidation({ valid: true, error: null });
    setUsernameAvailability(null);
    setPublicProfileSaveStatus(null);
  };

  // Check username availability (debounced)
  const checkUsernameAvailability = useCallback(async (usernameToCheck) => {
    // Don't check if it's the same as the original (user's current username)
    if (usernameToCheck.trim().toLowerCase() === (originalUsername || '').toLowerCase()) {
      setUsernameAvailability('available');
      return;
    }
    
    setUsernameAvailability('checking');
    
    try {
      const response = await api.get(`/user/check-username/${encodeURIComponent(usernameToCheck)}`);
      if (response.data.available) {
        setUsernameAvailability('available');
      } else {
        setUsernameAvailability('taken');
        setUsernameValidation({ valid: false, error: response.data.error });
      }
    } catch (error) {
      console.error('Error checking username:', error);
      if (error.response?.status === 400) {
        setUsernameAvailability('error');
        setUsernameValidation({ valid: false, error: error.response.data.detail });
      } else {
        setUsernameAvailability('error');
      }
    }
  }, [originalUsername]);

  // Handle username input change with validation and debounced availability check
  const handleUsernameChange = useCallback((value) => {
    setUsernameInput(value);
    setPublicProfileSaveStatus(null);
    
    // Clear previous timeout
    if (availabilityCheckTimeout.current) {
      clearTimeout(availabilityCheckTimeout.current);
    }
    
    // If empty, clear validation
    if (!value.trim()) {
      setUsernameValidation({ valid: true, error: null });
      setUsernameAvailability(null);
      return;
    }
    
    // Validate format first
    const validation = validateUsername(value);
    setUsernameValidation(validation);
    
    // Only check availability if format is valid
    if (validation.valid) {
      // Debounce availability check (500ms)
      availabilityCheckTimeout.current = setTimeout(() => {
        checkUsernameAvailability(value);
      }, 500);
    } else {
      setUsernameAvailability(null);
    }
  }, [checkUsernameAvailability]);

  // Copy the best available public URL (vanity if available)
  const copyPublicProfileLink = async () => {
    const url = getBestPublicUrl(user?.id, username);
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      toast.success(language === 'ptBR' ? 'Link copiado!' : 'Link copied!');
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  // Open public profile in new tab
  const openPublicProfile = () => {
    const url = getBestPublicUrl(user?.id, username);
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <GeniLoader />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-[#0A0A0A]/95 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Link 
              to="/dashboard"
              className="text-white/60 hover:text-white transition-colors"
            >
              <CaretLeft size={20} />
            </Link>
            <div className="flex items-center gap-3">
              <Gear size={24} className="text-[#00E676]" />
              <h1 className="text-xl font-bold">{t('nav.settings')}</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Public Profile Section */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
              <Globe size={20} className="text-cyan-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">
                {language === 'ptBR' ? 'Perfil Público' : 'Public Profile'}
              </h2>
              <p className="text-white/50 text-sm">
                {language === 'ptBR' 
                  ? 'Compartilhe seu desempenho com um link permanente'
                  : 'Share your performance with a permanent link'
                }
              </p>
            </div>
          </div>

          <div className="bg-[#121212] border border-white/10 rounded-lg p-6 space-y-6">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {publicProfileEnabled ? (
                  <Eye size={20} className="text-cyan-400" />
                ) : (
                  <Lock size={20} className="text-white/40" />
                )}
                <div>
                  <div className="font-medium flex items-center gap-2">
                    {language === 'ptBR' ? 'Ativar Perfil Público' : 'Enable Public Profile'}
                    {/* Status indicator */}
                    {hasPublicProfileChanges && !publicProfileSaveStatus && (
                      <span className="text-xs text-amber-400 font-normal">
                        • {language === 'ptBR' ? 'Alterações não salvas' : 'Unsaved changes'}
                      </span>
                    )}
                    {publicProfileSaveStatus === 'saving' && (
                      <span className="text-xs text-cyan-400 font-normal flex items-center gap-1">
                        <div className="w-3 h-3 border border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
                        {language === 'ptBR' ? 'Salvando...' : 'Saving...'}
                      </span>
                    )}
                    {publicProfileSaveStatus === 'saved' && (
                      <span className="text-xs text-green-400 font-normal flex items-center gap-1">
                        <Check size={12} weight="bold" />
                        {language === 'ptBR' ? 'Salvo' : 'Saved'}
                      </span>
                    )}
                    {publicProfileSaveStatus === 'error' && (
                      <span className="text-xs text-red-400 font-normal">
                        • {language === 'ptBR' ? 'Erro ao salvar' : 'Failed to save'}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-white/50">
                    {publicProfileEnabled 
                      ? (language === 'ptBR' ? 'Qualquer pessoa com o link pode ver seu perfil' : 'Anyone with the link can view your profile')
                      : (language === 'ptBR' ? 'Seu perfil está privado' : 'Your profile is private')
                    }
                  </div>
                </div>
              </div>
              <Switch
                checked={publicProfileEnabled}
                onCheckedChange={handlePublicProfileToggle}
                disabled={publicProfileSaving}
                className="data-[state=checked]:bg-cyan-500"
                data-testid="public-profile-toggle"
              />
            </div>

            {/* Lifecycle/Engagement Emails Section */}
            <div className="flex items-center justify-between pt-4 border-t border-white/10">
              <div className="flex items-center gap-3">
                <EnvelopeSimple size={20} className={lifecycleEmailsEnabled ? 'text-cyan-400' : 'text-white/40'} />
                <div>
                  <div className="font-medium">
                    {language === 'ptBR' ? 'Emails de acompanhamento' : 'Follow-up emails'}
                  </div>
                  <div className="text-sm text-white/50">
                    {language === 'ptBR'
                      ? 'Receba resumos, marcos e lembretes úteis sobre sua atividade no GENI.'
                      : 'Get useful summaries, milestones and reminders about your GENI activity.'}
                  </div>
                </div>
              </div>
              <Switch
                checked={lifecycleEmailsEnabled}
                onCheckedChange={handleLifecycleEmailsToggle}
                disabled={lifecycleSaving}
                className="data-[state=checked]:bg-cyan-500"
                data-testid="lifecycle-emails-toggle"
              />
            </div>

            {/* Profile Picture Section */}
            <div className="pt-4 border-t border-white/10 space-y-3">
              <div className="flex items-center gap-2">
                <Camera size={18} className="text-white/50" />
                <Label className="text-white/90 text-sm font-medium">
                  {language === 'ptBR' ? 'Foto de Perfil' : 'Profile Picture'}
                </Label>
                <span className="text-white/40 text-xs">
                  {language === 'ptBR' ? '(aparece no seu perfil público)' : '(shown on your public profile)'}
                </span>
              </div>
              
              <div className="flex items-center gap-4">
                {/* Avatar Preview */}
                <div className="relative">
                  <div 
                    className="w-20 h-20 rounded-full overflow-hidden border-2 border-white/20 bg-[#0A0A0A] flex items-center justify-center"
                    data-testid="avatar-preview"
                  >
                    <img
                      src={avatarUrl || GENI_BRAIN_FALLBACK}
                      alt="Profile"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = GENI_BRAIN_FALLBACK;
                      }}
                    />
                  </div>
                  {avatarUploading && (
                    <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                      <CircleNotch size={24} className="text-white animate-spin" />
                    </div>
                  )}
                </div>
                
                {/* Upload/Change/Remove Buttons */}
                <div className="flex flex-col gap-2">
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleAvatarSelect}
                    className="hidden"
                    data-testid="avatar-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => avatarInputRef.current?.click()}
                    disabled={avatarUploading}
                    className="border-white/20 text-white/70 hover:text-white hover:bg-white/10"
                    data-testid="avatar-upload-btn"
                  >
                    <Camera size={14} className="mr-2" />
                    {avatarUrl 
                      ? (language === 'ptBR' ? 'Trocar Imagem' : 'Change Image')
                      : (language === 'ptBR' ? 'Enviar Imagem' : 'Upload Image')
                    }
                  </Button>
                  
                  {avatarUrl && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveAvatar}
                      disabled={avatarUploading}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      data-testid="avatar-remove-btn"
                    >
                      <Trash size={14} className="mr-2" />
                      {language === 'ptBR' ? 'Remover' : 'Remove'}
                    </Button>
                  )}
                </div>
              </div>
              
              <p className="text-white/40 text-xs">
                {language === 'ptBR' 
                  ? 'JPG, PNG ou WEBP. Máximo 5MB.'
                  : 'JPG, PNG or WEBP. Max 5MB.'
                }
              </p>
            </div>

            {/* Username Input Section */}
            <div className="pt-4 border-t border-white/10 space-y-3">
              <div className="flex items-center gap-2">
                <At size={18} className="text-white/50" />
                <Label className="text-white/90 text-sm font-medium">
                  {language === 'ptBR' ? 'Nome de Usuário' : 'Username'}
                </Label>
                <span className="text-white/40 text-xs">
                  {language === 'ptBR' ? '(para seu link público)' : '(for your public link)'}
                </span>
              </div>
              
              <div className="space-y-2">
                <div className="relative">
                  <Input
                    value={usernameInput}
                    onChange={(e) => handleUsernameChange(e.target.value)}
                    placeholder={language === 'ptBR' ? 'escolha_seu_username' : 'choose_your_username'}
                    disabled={publicProfileSaving}
                    className={`bg-[#0A0A0A] border-white/10 text-white font-mono pr-10 ${
                      usernameInput && !usernameValidation.valid ? 'border-red-500/50' : ''
                    } ${
                      usernameInput && usernameValidation.valid && usernameAvailability === 'available' ? 'border-green-500/50' : ''
                    }`}
                    data-testid="username-input"
                  />
                  {/* Availability indicator */}
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {usernameAvailability === 'checking' && (
                      <CircleNotch size={18} className="text-cyan-400 animate-spin" />
                    )}
                    {usernameAvailability === 'available' && usernameValidation.valid && (
                      <CheckCircle size={18} weight="fill" className="text-green-400" />
                    )}
                    {(usernameAvailability === 'taken' || (usernameInput && !usernameValidation.valid)) && (
                      <XCircle size={18} weight="fill" className="text-red-400" />
                    )}
                  </div>
                </div>
                
                {/* Validation/Availability message */}
                {usernameInput && !usernameValidation.valid && (
                  <p className="text-red-400 text-xs flex items-center gap-1">
                    <XCircle size={12} />
                    {usernameValidation.error}
                  </p>
                )}
                {usernameAvailability === 'taken' && usernameValidation.valid && (
                  <p className="text-red-400 text-xs flex items-center gap-1">
                    <XCircle size={12} />
                    {language === 'ptBR' ? 'Este nome de usuário já está em uso' : 'This username is already taken'}
                  </p>
                )}
                {usernameAvailability === 'available' && usernameValidation.valid && usernameInput && (
                  <p className="text-green-400 text-xs flex items-center gap-1">
                    <CheckCircle size={12} />
                    {language === 'ptBR' ? 'Nome de usuário disponível!' : 'Username is available!'}
                  </p>
                )}
                
                {/* Vanity URL Preview */}
                {usernameInput && usernameValidation.valid && (
                  <div className="bg-white/5 rounded-md px-3 py-2 flex items-center gap-2">
                    <LinkSimple size={14} className="text-cyan-400/60" />
                    <span className="text-white/50 text-xs font-mono">
                      {getDisplayDomain()}/u/
                    </span>
                    <span className="text-cyan-400 text-xs font-mono font-medium">
                      {usernameInput.trim().toLowerCase()}
                    </span>
                  </div>
                )}
                
                {/* Helper text */}
                {!usernameInput && (
                  <p className="text-white/40 text-xs">
                    {language === 'ptBR' 
                      ? `${USERNAME_MIN_LENGTH}-30 caracteres. Letras, números, _ e - permitidos.`
                      : `${USERNAME_MIN_LENGTH}-30 characters. Letters, numbers, _ and - allowed.`
                    }
                  </p>
                )}
              </div>
            </div>

            {/* Save/Cancel buttons - only show when there are unsaved changes */}
            {hasPublicProfileChanges && (
              <div className="flex items-center gap-3 pt-2 border-t border-white/5">
                <Button
                  onClick={handleSavePublicProfile}
                  disabled={publicProfileSaving || (hasUsernameChanges && usernameInput.trim() && (!usernameValidation.valid || usernameAvailability === 'taken' || usernameAvailability === 'checking'))}
                  className="bg-cyan-500 text-black font-bold hover:bg-cyan-400 disabled:opacity-50"
                  data-testid="save-public-profile-btn"
                >
                  {publicProfileSaving ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      {language === 'ptBR' ? 'Salvando...' : 'Saving...'}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <FloppyDisk size={16} />
                      {language === 'ptBR' ? 'Salvar Alterações' : 'Save Changes'}
                    </span>
                  )}
                </Button>
                <Button
                  onClick={handleRevertPublicProfile}
                  variant="outline"
                  disabled={publicProfileSaving}
                  className="border-white/10 text-white/70 hover:bg-white/5"
                  data-testid="revert-public-profile-btn"
                >
                  {language === 'ptBR' ? 'Cancelar' : 'Cancel'}
                </Button>
              </div>
            )}

            {/* Public Profile Link - show when enabled AND saved */}
            {originalPublicProfileEnabled && (
              <div className="pt-4 border-t border-white/10 space-y-4">
                {/* Primary Link - Vanity URL if username exists */}
                {username ? (
                  <>
                    {/* Vanity URL (Primary) */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="text-white/90 text-sm font-medium">
                          {language === 'ptBR' ? 'Seu Link Público' : 'Your Public Link'}
                        </Label>
                        <span className="px-2 py-0.5 bg-[#00E676]/10 text-[#00E676] text-xs rounded-full font-medium">
                          {language === 'ptBR' ? 'Recomendado' : 'Recommended'}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Input
                          value={getVanityUrl(username)}
                          readOnly
                          onClick={(e) => e.target.select()}
                          className="bg-[#0A0A0A] border-[#00E676]/30 text-white font-mono text-sm"
                          data-testid="vanity-url-input"
                        />
                        <Button
                          onClick={copyPublicProfileLink}
                          variant="outline"
                          className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 flex-shrink-0"
                          title={language === 'ptBR' ? 'Copiar link' : 'Copy link'}
                          data-testid="copy-vanity-url-btn"
                        >
                          {linkCopied ? (
                            <Check size={16} className="text-green-400" />
                          ) : (
                            <Copy size={16} />
                          )}
                        </Button>
                        <Button
                          onClick={openPublicProfile}
                          variant="outline"
                          className="border-white/20 text-white/70 hover:bg-white/5 flex-shrink-0"
                          title={language === 'ptBR' ? 'Abrir perfil' : 'Open profile'}
                          data-testid="open-profile-btn"
                        >
                          <ArrowSquareOut size={16} />
                        </Button>
                      </div>
                      <p className="text-white/50 text-xs flex items-center gap-1">
                        <LinkSimple size={12} />
                        {language === 'ptBR' 
                          ? 'Link limpo e fácil de compartilhar'
                          : 'Clean, easy-to-share link'
                        }
                      </p>
                    </div>

                    {/* Fallback URL (Secondary) */}
                    <div className="space-y-2 pt-3 border-t border-white/5">
                      <Label className="text-white/50 text-xs">
                        {language === 'ptBR' ? 'Link alternativo (ID)' : 'Alternative link (ID-based)'}
                      </Label>
                      <Input
                        value={getPublicProfileUrl(user?.id)}
                        readOnly
                        onClick={(e) => e.target.select()}
                        className="bg-[#0A0A0A] border-white/10 text-white/60 font-mono text-xs"
                        data-testid="fallback-url-input"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    {/* No username - show ID-based URL as primary */}
                    <div className="space-y-2">
                      <Label className="text-white/70 text-sm">
                        {language === 'ptBR' ? 'Seu Link Público' : 'Your Public Link'}
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          value={getPublicProfileUrl(user?.id)}
                          readOnly
                          onClick={(e) => e.target.select()}
                          className="bg-[#0A0A0A] border-white/10 text-white/80 font-mono text-sm"
                          data-testid="public-profile-url-input"
                        />
                        <Button
                          onClick={copyPublicProfileLink}
                          variant="outline"
                          className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 flex-shrink-0"
                          title={language === 'ptBR' ? 'Copiar link' : 'Copy link'}
                          data-testid="copy-public-profile-link-btn"
                        >
                          {linkCopied ? (
                            <Check size={16} className="text-green-400" />
                          ) : (
                            <Copy size={16} />
                          )}
                        </Button>
                        <Button
                          onClick={openPublicProfile}
                          variant="outline"
                          className="border-white/20 text-white/70 hover:bg-white/5 flex-shrink-0"
                          title={language === 'ptBR' ? 'Abrir perfil' : 'Open profile'}
                          data-testid="open-profile-btn"
                        >
                          <ArrowSquareOut size={16} />
                        </Button>
                      </div>
                    </div>

                    {/* Hint to set username */}
                    <div className="bg-white/5 border border-white/10 rounded-lg p-3 flex items-start gap-2">
                      <User size={16} className="text-white/40 mt-0.5 flex-shrink-0" />
                      <div className="text-xs text-white/50">
                        <p>
                          {language === 'ptBR' 
                            ? 'Defina um nome de usuário para obter um link mais curto como:'
                            : 'Set a username to get a shorter link like:'
                          }
                        </p>
                        <p className="text-cyan-400/70 font-mono mt-1">
                          {getDisplayDomain()}/u/seu-username
                        </p>
                      </div>
                    </div>
                  </>
                )}

                <p className="text-white/40 text-xs">
                  {language === 'ptBR' 
                    ? 'Este link é permanente e sempre aponta para seu perfil atual.'
                    : 'This link is permanent and always points to your current profile.'
                  }
                </p>
              </div>
            )}

            {/* Pending enable notification */}
            {publicProfileEnabled && !originalPublicProfileEnabled && hasPublicProfileChanges && (
              <div className="pt-4 border-t border-white/10">
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 flex items-start gap-2">
                  <Warning size={16} className="text-amber-400 mt-0.5 flex-shrink-0" />
                  <p className="text-amber-400/90 text-sm">
                    {language === 'ptBR' 
                      ? 'Clique em "Salvar Alterações" para ativar seu perfil público e obter seu link.'
                      : 'Click "Save Changes" to enable your public profile and get your link.'
                    }
                  </p>
                </div>
              </div>
            )}

            {/* Info Banner */}
            <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-4 flex items-start gap-3">
              <Info size={18} className="text-cyan-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-white/70">
                <p className="mb-2 font-medium text-white/90">
                  {language === 'ptBR' ? 'O que é compartilhado:' : 'What is shared:'}
                </p>
                <ul className="space-y-1 text-white/50">
                  <li>• {language === 'ptBR' ? 'Dashboard, Posições, Histórico, Analytics' : 'Dashboard, Positions, History, Analytics'}</li>
                  <li>• {language === 'ptBR' ? 'Win Rate, Avg RR, Profit Factor' : 'Win Rate, Avg RR, Profit Factor'}</li>
                  <li>• {language === 'ptBR' ? 'Performance geral e métricas' : 'Overall performance and metrics'}</li>
                </ul>
                <p className="mt-2 text-cyan-400/80">
                  <Lock size={12} className="inline mr-1" />
                  {language === 'ptBR' 
                    ? 'Valores em USD (Hand Size, P&L) ficam ocultos'
                    : 'USD values (Hand Size, P&L) remain hidden'
                  }
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Integrations Section */}
        <section className="mb-12 border-t border-white/5 pt-12">
          <IntegrationsSettings language={language} isPro={isPro} />
        </section>
      </div>
      
      {/* Image Cropper Modal */}
      {showCropper && cropperFile && (
        <ImageCropper
          imageFile={cropperFile}
          onSave={handleCroppedImageSave}
          onCancel={handleCropperCancel}
          language={language}
        />
      )}
    </div>
  );
}
